/// <amd-module name="Wit/WorkItemBuildLinks/Components/BuildLinkForm" />
import * as React from "react";
import { useCallback, useRef, useState } from "react";

import { format } from "VSS/Core/Util/String";
import { usePageContext } from "VSS/Platform/Layout";
import { IVssHistoryService } from "VSS/Platform/History";
import { useObservable } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { TextField } from "VSSUI/TextField";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";

import { ArtifactsUpdatedCallback } from "Wit/WorkItemLinksRegistration/IRegisteredLinkTypeService";
import { IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";

import * as Resources from "Wit/WorkItemBuildLinks/Resources";
import { BuildPickerDialog } from "./BuildPickerDialog";
import { Build } from "Build/Client/Contracts/Build";
import { getBuildClient, IBuildRestClient } from "Build/Client/RestClient/Build";

interface IBuildLinkFormProps {
    onArtifactsUpdated: ArtifactsUpdatedCallback;
    linkType: string;
    getDisplayData: (build: Build, linkType: string) => IResolvedLinkDisplayData;
}

const timerManagement = new TimerManagement();

export function BuildLinkForm({ linkType, onArtifactsUpdated, getDisplayData }: IBuildLinkFormProps) {
    const [buildPickerDialogOpen, setBuildPickerDialogOpen] = useObservable<boolean>(false);
    const [buildNumberText, setBuildNumberText] = useObservable<string>("");
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const pageContext = usePageContext();
    const buildClient = useRef<IBuildRestClient>(getBuildClient(pageContext));
    const historyService = useRef<IVssHistoryService>(pageContext.getService<IVssHistoryService>("IVssHistoryService"));

    const updateBuildNumberText = async (newText: string) => {
        const project = historyService.current!.getState()["project"];
        if (typeof project !== "string") {
            return;
        }

        if (!newText.trim()) {
            onArtifactsUpdated([]);
            setErrorMessage(Resources.MissingBuildNumberError);
            return;
        }

        const build = await getBuildByNumber(buildClient.current, project, newText);
        if (buildNumberText.value !== newText) {
            // The input has changed since we sent the request; do nothing with the response
            return;
        }

        if (!build) {
            onArtifactsUpdated([]);
            setErrorMessage(format(Resources.CantResolveBuildNumberError, buildNumberText.value));
            return;
        }

        const artifact = getDisplayData(build, linkType);
        onArtifactsUpdated([artifact]);
        setErrorMessage(undefined);
    };
    const throttledUpdateBuildNumberText: Function = timerManagement.debounce(updateBuildNumberText, 400);

    const selectBuild = useCallback(
        (build: Build | undefined) => {
            buildNumberText.value = build?.buildNumber ?? "";
            setBuildPickerDialogOpen(false);

            if (!build) {
                onArtifactsUpdated([]);
                setErrorMessage(format(Resources.CantResolveBuildNumberError, buildNumberText.value));
                return;
            }

            const artifact = getDisplayData(build, linkType);
            onArtifactsUpdated([artifact]);
            setErrorMessage(undefined);
        },
        [linkType, setBuildPickerDialogOpen, onArtifactsUpdated, setErrorMessage, getDisplayData]
    );

    return (
        <>
            <FormItem className="build-link-form-item" label={Resources.BuildNumberLabel} error={!!errorMessage} message={errorMessage}>
                <div className="flex-row flex-wrap gap-8">
                    <TextField
                        containerClassName="flex-grow"
                        value={buildNumberText}
                        onChange={(e, val) => {
                            setBuildNumberText(val);
                            throttledUpdateBuildNumberText(val);
                        }}
                    />
                    <Button text={Resources.FindBuildsButtonLabel} onClick={() => setBuildPickerDialogOpen(true)} />
                </div>
            </FormItem>

            <ConditionalChildren renderChildren={buildPickerDialogOpen}>
                <BuildPickerDialog onBuildChosen={selectBuild} onDismiss={() => setBuildPickerDialogOpen(false)} />
            </ConditionalChildren>
        </>
    );
}

function getBuildByNumber(buildClient: IBuildRestClient, project: string, buildNumber: string): Promise<Build | undefined> {
    return buildClient
        .getBuilds(project, undefined, undefined, buildNumber)
        .then(builds => builds[0]) // There should be at most one matching build
        .catch(() => undefined); // In case the build is not found or there is some other error
}
